import React, { useState, useEffect, useRef, useCallback } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import TabContainerHOC from './hocs/TabContainerHOC';
import Notification from '../global/Notification';
import StickySaveBar from '../global/StickySaveBar';
import { PageProps } from '../../types';
import ValueRow, { ValueElement } from './values/ValueRow';
import { AVAILABLE_ICON_IDS, ICON_MAP } from './values/iconMapper';
import IconPickerModal from './values/IconPickerModal';
import ValuesLivePreview from './values/ValuesLivePreview';
import { useUnsavedChangesGuard } from './hooks/useUnsavedChangesGuard';
import { getCustomizationSection, saveCustomizationSection, uploadCustomizationImage } from '../../lib/customization/customizationService';

export interface WhyChooseUsData {
    sectionTitle: string;
    elements: ValueElement[];
}

const defaultData: WhyChooseUsData = {
    sectionTitle: '',
    elements: [],
};

export default function ValuesPage({ model }: PageProps) {
    const [valuesData, setValuesData] = useState<WhyChooseUsData>(defaultData);
    const [originalData, setOriginalData] = useState<WhyChooseUsData | null>(null);
    const [pickingIconForId, setPickingIconForId] = useState<string | null>(null);
    
    const [uploadingElements, setUploadingElements] = useState<Record<string, boolean>>({});
    const [calling, setCalling] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    const [notification, setNotification] = useState<{ message: string | null; type: 'success' | 'error' }>({
        message: null,
        type: 'success'
    });
    const [saveError, setSaveError] = useState<string | null>(null);
    const [dragIndex, setDragIndex] = useState<number | null>(null);
    const [bodyOverflow, setBodyOverflow] = useState<string | null>(null);


    const closeNotification = useCallback(() => setNotification({ message: null, type: 'success' }), []);
    const dismissSaveError = useCallback(() => setSaveError(null), []);

    const onDragStartCard = (idx: number, e: React.DragEvent) => {
        setDragIndex(idx);
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", String(idx));
        const card = (e.currentTarget as HTMLElement).closest(".value-row-container") as HTMLElement | null;
        if (card) {
            const rect = card.getBoundingClientRect();
            e.dataTransfer.setDragImage(card, e.clientX - rect.left, e.clientY - rect.top);
        }
    };

    const onDragOverCard = (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
    };

    const onDropOnCard = (idx: number, e: React.DragEvent) => {
        e.preventDefault();
        const from = dragIndex ?? parseInt(e.dataTransfer.getData("text/plain"), 10);
        if (isNaN(from) || from === idx) {
            setDragIndex(null);
            return;
        }

        const nextElements = [...valuesData.elements];
        const tmp = nextElements[from];
        nextElements[from] = nextElements[idx];
        nextElements[idx] = tmp;
        const nextData = { ...valuesData, elements: nextElements };
        
        setValuesData(nextData);
        setHasChanges(checkForChanges(nextData));
        setDragIndex(null);
    };

    const onDragEndCard = () => setDragIndex(null);

    const onTouchStartCard = (idx: number) => {
        if (typeof document !== "undefined") {
            if (bodyOverflow === null) {
                setBodyOverflow(document.body.style.overflow || "");
            }
            document.body.style.overflow = "hidden";
        }
        setDragIndex(idx);
    };

    const onTouchMoveCard = (e: React.TouchEvent) => {
        if (dragIndex === null) return;
        const touch = e.touches[0];
        const element = document.elementFromPoint(touch.clientX, touch.clientY);
        const card = element?.closest(".value-row-container") as HTMLElement | null;
        if (!card) return;
        const overIndex = parseInt(card.getAttribute("data-index") || "-1", 10);
        if (overIndex < 0 || overIndex === dragIndex) {
            e.preventDefault();
            return;
        }

        const nextElements = [...valuesData.elements];
        const tmp = nextElements[dragIndex];
        nextElements[dragIndex] = nextElements[overIndex];
        nextElements[overIndex] = tmp;
        const nextData = { ...valuesData, elements: nextElements };

        setValuesData(nextData);
        setHasChanges(checkForChanges(nextData));
        setDragIndex(overIndex);
        e.preventDefault();
    };

    const onTouchEndCard = () => {
        if (typeof document !== "undefined" && bodyOverflow !== null) {
            document.body.style.overflow = bodyOverflow;
        }
        setDragIndex(null);
    };

    const fetchValues = React.useCallback(async () => {
        setCalling(true);
        try {
            const data = await getCustomizationSection<any>(model.storeId, 'WhyChooseUs');
            
            const apiData = data || {};
            const baseline: WhyChooseUsData = { 
                sectionTitle: apiData.sectionTitle || '', 
                elements: apiData.elements || [] 
            };
            
            setValuesData(baseline);
            setOriginalData(baseline);
            setHasChanges(false);
        } catch (error) {
            console.error('Error fetching Values:', error);
            setNotification({ message: 'Failed to load values data.', type: 'error' });
        } finally {
            setCalling(false);
        }
    }, [model.storeId]);

    useEffect(() => {
        if (model?.storeId) fetchValues();
    }, [model?.storeId, fetchValues]);

    const checkForChanges = (newData: WhyChooseUsData) => {
        if (!originalData) return false;
        return JSON.stringify(newData) !== JSON.stringify(originalData);
    };

    const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const updatedData = { ...valuesData, sectionTitle: e.target.value };
        setValuesData(updatedData);
        setHasChanges(checkForChanges(updatedData));
    };

    const handleElementChange = (id: string, field: keyof ValueElement, value: string | boolean) => {
        const updatedElements = valuesData.elements.map(el => 
            el.id === id ? { ...el, [field]: value } : el
        );
        const updatedData = { ...valuesData, elements: updatedElements };
        setValuesData(updatedData);
        setHasChanges(checkForChanges(updatedData));
    };

    const handleIconSelect = async (iconId: string) => {
        if (!pickingIconForId) return;
        const elementId = pickingIconForId;
        
        setPickingIconForId(null);
        
        const IconComponent = ICON_MAP[iconId];
        if (!IconComponent) return;

        setUploadingElements(prev => ({ ...prev, [elementId]: true }));

        try {
            const svgString = renderToStaticMarkup(<IconComponent />);
            const file = new File([svgString], `${iconId}.svg`, { type: 'image/svg+xml' });
            
            const url = await uploadCustomizationImage(model.storeId, file);

            handleElementChange(elementId, 'imageUrl', url);
            setNotification({ message: 'Icon uploaded successfully!', type: 'success' });

        } catch (error) {
            console.error('Error uploading icon:', error);
            setNotification({ message: "Icon upload failed.", type: 'error' });
        } finally {
            setUploadingElements(prev => ({ ...prev, [elementId]: false }));
        }
    };

    const justAddedElementRef = useRef(false);

    const addElement = () => {
        const newElement: ValueElement = {
            id: Date.now().toString(),
            imageUrl: '',
            title: '',
            subtitle: '',
            url: '',
            isActive: true
        };
        const updatedData = { ...valuesData, elements: [...valuesData.elements, newElement] };
        justAddedElementRef.current = true;
        setValuesData(updatedData);
        setHasChanges(checkForChanges(updatedData));
    };

    useEffect(() => {
        if (!justAddedElementRef.current) return;
        justAddedElementRef.current = false;

        const newIndex = valuesData.elements.length - 1;
        if (newIndex < 0) return;

        const id = requestAnimationFrame(() => {
            const row = document.querySelector(
                `.value-row-container[data-index="${newIndex}"]`
            );
            row?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
        return () => cancelAnimationFrame(id);
    }, [valuesData.elements.length]);

    const removeElement = (id: string) => {
        const updatedElements = valuesData.elements.filter(el => el.id !== id);
        const updatedData = { ...valuesData, elements: updatedElements };
        setValuesData(updatedData);
        setHasChanges(checkForChanges(updatedData));
    };

    const saveValues = async (): Promise<boolean> => {
        setCalling(true);
        setSaveError(null);

        try {
            await saveCustomizationSection({
                storeId: model.storeId,
                metafield: 'WhyChooseUs',
                section: 'whyChooseUs',
                payload: valuesData,
                lastValue: originalData,
            });

            setNotification({ message: 'Values saved successfully!', type: 'success' });
            setOriginalData(valuesData);
            setHasChanges(false);
            return true;
        } catch (error) {
            console.error('Error saving values:', error);
            setSaveError('Failed to save values.');
            return false;
        } finally {
            setCalling(false);
        }
    };

    const unsavedChangesModal = useUnsavedChangesGuard({ hasChanges, save: saveValues });

    const handleCancel = () => {
        if (originalData) {
            setValuesData(originalData);
            setHasChanges(false);
        }
        setSaveError(null);
    };

    const isAnyUploading = Object.values(uploadingElements).some(Boolean);

    return (
        <>
            <TabContainerHOC title="Values - Why Choose Us" description={<Description />} containerClass="values" loading={calling}>

                {pickingIconForId && (
                    <IconPickerModal
                        availableIcons={AVAILABLE_ICON_IDS}
                        onClose={() => setPickingIconForId(null)}
                        onSelect={handleIconSelect}
                    />
                )}

                <div className="values-form-container">
                    <div className="row title-inputs mb-2">
                        <div className="col-sm col-left">
                            <h2>Edit the Site Values Section</h2>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-sm-12 col-left">
                            <div className="field-group">
                                <p className="field-label"><b>Section Title (Optional)</b></p>
                                <p className="field-hint">The main heading for this section</p>
                                <input 
                                    value={valuesData.sectionTitle} 
                                    onChange={handleTitleChange} 
                                    disabled={calling || isAnyUploading} 
                                    type="text" 
                                    className="input-round form-control" 
                                    placeholder="e.g. Why Choose Us" 
                                />
                            </div>
                        </div>
                    </div>

                    <hr className="section-divider my-4" />

                    <div className="elements-section">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                                <h2>Elements</h2>
                                {valuesData.elements.length === 0 && (
                                    <p className="field-hint">
                                        No elements added yet. Click "+ Add New Element" to start.
                                    </p>
                                )}
                            </div> 
                            <button 
                                className="btn btn-add-element" 
                                onClick={addElement} 
                                disabled={calling || isAnyUploading}
                            >
                                + Add New Element
                            </button>
                        </div>

                        <div className="elements-wrapper">
                            {valuesData.elements.map((element, index) => (
                                <ValueRow 
                                    key={element.id}
                                    element={element} 
                                    index={index}
                                    onChange={handleElementChange} 
                                    onRemove={removeElement}
                                    disabled={calling}
                                    isUploading={!!uploadingElements[element.id]}
                                    onOpenIconPicker={() => setPickingIconForId(element.id)} 
                                    isDragging={dragIndex === index}
                                    onDragStart={onDragStartCard}
                                    onDragOver={onDragOverCard}
                                    onDrop={onDropOnCard}
                                    onDragEnd={onDragEndCard}
                                    onTouchStart={onTouchStartCard}
                                    onTouchMove={onTouchMoveCard}
                                    onTouchEnd={onTouchEndCard}
                                 />
                            ))}
                        </div>
                    </div>
                </div>

                <hr className="section-divider my-4" />

                <ValuesLivePreview valuesData={valuesData} />

                {unsavedChangesModal}
            </TabContainerHOC>
            <StickySaveBar
                visible={hasChanges}
                saving={calling}
                disabled={isAnyUploading}
                onSave={saveValues}
                onCancel={handleCancel}
                alignWithSelector=".leftVertical"
                errorMessage={saveError ?? undefined}
                onErrorDismiss={dismissSaveError}
            />
            <Notification message={notification.message} type={notification.type} onClose={closeNotification} />
        </>
    );
}

const Description = () => (
    <ul>
        <li>
            Provide a <b>Section Title</b> to introduce the "Why Choose Us" area.
        </li>
        <li>
            Add <b>unlimited elements</b>. Each element includes an icon, title, subtitle, and an optional link.
        </li>
        <li>
            Use the <b>toggle</b> to enable or disable specific elements without deleting them.
        </li>
        <li>
            Drag an element using the <b>☰</b> icon to <b>reorder</b> it in the grid.
        </li>
    </ul>
);